import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'

/**
 * The fake checkout. Stands in for Mercado Pago's hosted page during local
 * development, so the whole payment flow is clickable with no account.
 *
 * The buttons do NOT sign a webhook here -- the signing secret lives on the
 * server, and shipping it to the browser to save a round trip would be exactly
 * the wrong shortcut. They ask the backend to settle the payment, and it
 * delivers a properly-signed webhook to its own handler. So clicking Pay
 * exercises the same signature verification the real provider will.
 *
 * The backend endpoint is mounted only when APP_ENV is local AND the provider
 * is fake, so this page has nothing to talk to in a real deployment.
 */
export const Route = createFileRoute('/dev/pay/$paymentId')({
  validateSearch: (search: Record<string, unknown>): { return_url?: string } => ({
    return_url: typeof search.return_url === 'string' ? search.return_url : undefined,
  }),
  component: DevPayPage,
})

type Outcome = 'paid' | 'declined'

function DevPayPage() {
  const { paymentId } = Route.useParams()
  const navigate = useNavigate()
  const [busy, setBusy] = useState<Outcome | null>(null)
  const [error, setError] = useState('')

  async function settle(outcome: Outcome) {
    setBusy(outcome)
    setError('')
    try {
      const response = await fetch(`/api/dev/payments/${paymentId}/settle`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ outcome }),
      })
      if (!response.ok) throw new Error(`settle failed: ${response.status}`)
      void navigate({ to: '/bookings', search: { tab: 'upcoming' } })
    } catch (caught) {
      setError(String(caught))
      setBusy(null)
    }
  }

  return (
    <div className="mx-auto max-w-sm space-y-6">
      <div className="rounded-lg border-2 border-dashed border-amber-400 bg-amber-50 p-4">
        <p className="text-sm font-medium text-amber-900">Fake checkout</p>
        <p className="mt-1 text-xs text-amber-800">
          Local development only. No money moves. This stands in for Mercado Pago until it is wired
          up.
        </p>
      </div>

      <div>
        <h1 className="text-2xl font-semibold">Pagar la reserva</h1>
        <p className="mt-1 font-mono text-xs break-all text-slate-500">{paymentId}</p>
      </div>

      <div className="space-y-2">
        <button
          onClick={() => settle('paid')}
          disabled={busy !== null}
          className="min-h-12 w-full rounded-md bg-green-700 px-4 text-white hover:bg-green-800 disabled:opacity-50"
        >
          {busy === 'paid' ? 'Paying…' : 'Pay'}
        </button>
        <button
          onClick={() => settle('declined')}
          disabled={busy !== null}
          className="min-h-12 w-full rounded-md bg-red-700 px-4 text-white hover:bg-red-800 disabled:opacity-50"
        >
          {busy === 'declined' ? 'Declining…' : 'Decline'}
        </button>
        <button
          onClick={() => void navigate({ to: '/bookings', search: { tab: 'upcoming' } })}
          disabled={busy !== null}
          className="min-h-12 w-full rounded-md border border-slate-300 px-4 hover:bg-slate-50 disabled:opacity-50"
        >
          Abandon
        </button>
        <p className="text-center text-xs text-slate-500">
          Abandoning leaves the hold to expire on its own, which is what the release job is for.
        </p>
      </div>

      {error && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
    </div>
  )
}
